import isEqual from 'lodash/isEqual';
import { makeObservable, override } from 'mobx';

import type { Optional, RenderedServiceData, ServiceCategory, ServiceData } from 'chaika-frontend-types/frontend-cms';
import { Currency } from 'chaika-enums';

import { store } from '@store';
import { assertIsDefined } from '@utils/assertIsDefined';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { RenderedServiceList } from '@store/models/SystemFields/RenderedService';

import { AbstractField } from './AbstractField';
import { translate } from '@chaika/i18n/helper';

export interface TotalPriceParams {
    currency: Currency;
    totalServicesCount: number;
    totalAmountToPay: number;
    totalBasePriceDiscount: number;
    totalInsuranceAmount: number;
    individualDiscount: number;
    totalGuaranteeLetterAmount: number;
    totalDiscount: number;
    totalCost: number;
}

export class RenderedServiceField extends AbstractField<RenderedServiceList, RenderedServiceData[]> {
    constructor(params: RenderedServiceData[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.getSize() > 0;
    }

    public getTotalPriceParams(): TotalPriceParams[] {
        const totalPrices = new Map<Currency, Omit<TotalPriceParams, 'currency' | 'totalDiscount'>>();
        const totalPricesParams: TotalPriceParams[] = [];

        this.fieldValue.values.forEach(
            ({
                id,
                currency,
                count,
                basePriceDiscount = 0,
                amountToPay,
                insuranceAmount = 0,
                totalCost,
                individualDiscount = 0,
                corporateDiscount = 0,
            }) => {
                const currencyTotalPrice = totalPrices.get(currency) ?? {
                    totalAmountToPay: 0,
                    totalCost: 0,
                    totalBasePriceDiscount: 0,
                    totalGuaranteeLetterAmount: 0,
                    totalInsuranceAmount: 0,
                    totalServicesCount: 0,
                    individualDiscount: 0,
                };

                currencyTotalPrice.totalServicesCount += count;
                currencyTotalPrice.totalAmountToPay += amountToPay;
                currencyTotalPrice.individualDiscount += individualDiscount < 0 ? 0 : individualDiscount;
                currencyTotalPrice.totalBasePriceDiscount += Math.abs(basePriceDiscount);
                currencyTotalPrice.totalCost += totalCost;

                if (this.hasRenderedServiceGuaranteeLetterPlan(id)) {
                    currencyTotalPrice.totalGuaranteeLetterAmount += insuranceAmount;
                    currencyTotalPrice.totalGuaranteeLetterAmount += corporateDiscount;
                } else {
                    currencyTotalPrice.totalInsuranceAmount += insuranceAmount;
                    currencyTotalPrice.totalInsuranceAmount += corporateDiscount;
                }

                totalPrices.set(currency, currencyTotalPrice);
            },
        );

        totalPrices.forEach((params, currency) => {
            totalPricesParams.push({
                currency,
                ...params,
                totalDiscount: params.totalCost - params.totalAmountToPay,
            });
        });

        return totalPricesParams;
    }

    public hasRenderedServiceGuaranteeLetterPlan(id: string): boolean {
        const { planStore } = store;
        const { plans = [] } = this.fieldValue.values.find(value => value.id === id) ?? {};

        return !!plans.find(({ id }) => planStore.planList.has(id) && planStore.isGuaranteeLetterPlan(id));
    }

    public getItem(id: string): Optional<GroupedListParams<RenderedServiceData>> {
        return this.fieldValue.get(id);
    }

    public getItemTitle(id: string): string {
        const { isFinedService } = this.fieldValue.getExisting(id);
        const service = this.getRenderedServiceService(id);
        const prefix = isFinedService ? `${translate('bookingDoctorTime')}. ` : '';

        return `${prefix}${service ? service.code : ''}`;
    }

    public getItemCategory(id: string): ServiceCategory {
        const service = this.getRenderedServiceService(id);
        assertIsDefined(service, `Service of rendered service ${id} must be loaded before its category is read`);
        return service.category;
    }

    public getItemContent(id: string): string {
        return this.getRenderedServiceServiceTitle(id);
    }

    public getPreview(): string {
        return this.fieldValue.ids.map(id => this.getItemRevision(id)).join(', ');
    }

    public getItemRevision(id: string): string {
        const { count } = this.fieldValue.getExisting(id);
        const serviceName = this.getRenderedServiceServiceTitle(id);

        return `${serviceName} ${count}`;
    }

    public isEqual(value: RenderedServiceList): boolean {
        const renderedServices = this.makeUpdateParams();

        return isEqual(renderedServices, value.serialize());
    }

    public makeUpdateParams(): RenderedServiceData[] {
        return this.fieldValue.values;
    }

    protected setFieldValue(value: RenderedServiceData[]): void {
        this.fieldValue = new RenderedServiceList(value);
    }

    private getRenderedServiceServiceTitle(id: string): string {
        const { serviceStore } = store;
        const { serviceId, toothNumber } = this.fieldValue.getExisting(id);

        return `${serviceStore.serviceList.get(serviceId)?.name}${
            !!toothNumber ? ` (${translate('tooth').toLowerCase()}: ${toothNumber})` : ''
        }`;
    }

    private getRenderedServiceService(id: string): Optional<ServiceData> {
        const { serviceStore } = store;
        const { serviceId } = this.fieldValue.getExisting(id);

        return serviceStore.serviceList.get(serviceId);
    }
}
